import { Capabilities, ControlFlags, FeatureSet, PlayerContent, SurfaceKind } from './types'

export function playbackPathFor(caps: Capabilities): 'mse' | 'native' {
  return caps.mse ? 'mse' : 'native'
}

// Devices we are willing to render the sphere on optimistically before any
// probe: anything with WebGL. There is deliberately no rule about the device
// or about how the video is fed.
//
// From March to October 2026 this excluded iPhone, and required MSE, on the
// belief that Safari's native HLS renders black as a WebGL texture. That was
// never measured on a device; it came from WebKit bugs fixed in 2020-21. In
// October 2026 the sphere was tried on two iPhones and an iPad — native HLS,
// cross-origin from Bunny, through this player — and worked on all three. A
// device that really cannot texture the video is caught by the runtime probe
// (probeTexture), which measures instead of guessing. Do not add a device
// rule back here without a failing device in hand.
export function staticLikely360(caps: Capabilities): boolean {
  return caps.webgl2
}

// Whether the sphere can actually be rendered right now, given what we know
// about the texture (from cache or probe).
export function canRenderSphere(caps: Capabilities): boolean {
  return caps.webgl2 && caps.textureLive === true
}

export function initialTextureLive(caps: Capabilities, cached: boolean | undefined): boolean {
  if (cached !== undefined) return cached
  return staticLikely360(caps)
}

// We only probe when we optimistically showed the sphere without a cached
// confirmation — i.e. a static-likely-360 device on its first visit.
export function shouldVerifySphere(caps: Capabilities, cached: boolean | undefined): boolean {
  return cached === undefined && staticLikely360(caps)
}

export function resolveFeatures(
  caps: Capabilities,
  _content: PlayerContent,
  override?: SurfaceKind,
): FeatureSet {
  const canSphere = canRenderSphere(caps)
  const defaultSurface: SurfaceKind = override ?? (canSphere ? 'sphere' : 'flat')
  return { defaultSurface, canSphere, playbackPath: playbackPathFor(caps) }
}

export function resolveControls(
  activeSurface: SurfaceKind,
  caps: Capabilities,
  content: PlayerContent,
): ControlFlags {
  const sphere = activeSurface === 'sphere'
  const mse = playbackPathFor(caps) === 'mse'
  return {
    customChrome: true,
    subtitles: content.hasSubtitles,
    transcript: content.hasSubtitles,
    compass: sphere,
    viewPresets: sphere,
    follow: sphere && content.hasParticipants,
    gyro: sphere && caps.gyro !== 'unavailable' && caps.touch,
    // Never on iPhone: tapping an HLS element into Web Audio there yields
    // silence, and the tap cannot be undone for the life of the element.
    spatialAudio: sphere && content.hasParticipants && caps.webAudio && mse && !caps.isIPhone,
    // Both surfaces can expose HLS quality levels when VHS (MSE) drives
    // playback; the UI hides the menu when there are no selectable levels
    // (e.g. native HLS on iOS), so gating on mse alone is enough.
    qualitySelector: mse,
    // Gated on static capability (not canRenderSphere) so a texture-probe
    // downgrade still leaves the user a manual way back to 360.
    switchTo360: !sphere && staticLikely360(caps),
  }
}
